/**
 * useStyleConfig Hook
 * Resolves component styles from theme
 */

import { useTheme } from '../theme';
import { useColorMode } from '../system/color-mode';

/**
 * useStyleConfig Hook
 * Gets styles for a specific component from the theme
 * 
 * @param {string} componentName - Name of the component in theme
 * @param {object} props - Props to pass to style functions
 * 
 * @returns {object} - Resolved styles for the component
 * 
 * @example
 * const styles = useStyleConfig('Card', { variant: 'elevated' });
 * return <Box __css={styles} />;
 */
export const useStyleConfig = (componentName, props = {}) => {
  const theme = useTheme();
  const { colorMode } = useColorMode();
  
  const componentStyles = theme?.components?.[componentName];
  
  if (!componentStyles) {
    return {};
  }

  const propsWithDefaults = {
    colorMode,
    ...componentStyles.defaultProps,
    ...props,
  };

  // Resolve baseStyle
  let styles = {};
  
  if (componentStyles.baseStyle) {
    const baseStyle = typeof componentStyles.baseStyle === 'function'
      ? componentStyles.baseStyle(propsWithDefaults)
      : componentStyles.baseStyle;
    styles = { ...styles, ...baseStyle };
  }

  // Resolve sizes
  if (componentStyles.sizes && propsWithDefaults.size) {
    const sizeStyles = componentStyles.sizes[propsWithDefaults.size];
    if (sizeStyles) {
      const resolvedSize = typeof sizeStyles === 'function'
        ? sizeStyles(propsWithDefaults)
        : sizeStyles;
      styles = { ...styles, ...resolvedSize };
    }
  }

  // Resolve variants
  if (componentStyles.variants && propsWithDefaults.variant) {
    const variantStyles = componentStyles.variants[propsWithDefaults.variant];
    if (variantStyles) {
      const resolvedVariant = typeof variantStyles === 'function'
        ? variantStyles(propsWithDefaults)
        : variantStyles;
      styles = { ...styles, ...resolvedVariant };
    }
  }

  return styles;
};

/**
 * useMultiStyleConfig Hook
 * Gets styles for components with multiple parts (e.g., Modal)
 * 
 * @param {string} componentName - Name of the component in theme
 * @param {object} props - Props to pass to style functions
 * 
 * @returns {object} - Object with styles for each part
 */
export const useMultiStyleConfig = (componentName, props = {}) => {
  const theme = useTheme();
  const { colorMode } = useColorMode();
  
  const componentStyles = theme?.components?.[componentName];
  
  if (!componentStyles) {
    return {};
  }

  const propsWithDefaults = {
    colorMode,
    ...componentStyles.defaultProps,
    ...props,
  };

  // For multi-part components, baseStyle returns an object of parts
  if (componentStyles.baseStyle) {
    const baseStyle = typeof componentStyles.baseStyle === 'function'
      ? componentStyles.baseStyle(propsWithDefaults)
      : componentStyles.baseStyle;
    return baseStyle;
  }

  return {};
};

export default useStyleConfig;
